'use client';

import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { cn } from '@/lib/utils';
// PHASE-11 START
import Image from 'next/image';
import { getImagePlaceholder } from '@/lib/image-placeholder';
// PHASE-11 END
import AgentHandoffAnimation from '@/components/chat/AgentHandoffAnimation';
import { MessageFeedbackControls } from '@/components/chat/MessageFeedbackControls';
import { PencilIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import { InputV2 } from '@/components/ui/InputV2';
import { useClient } from '@/context/ClientContext';
import { useAuth } from '@/hooks/useAuth';
import { agentConfigService } from '@/lib/aiAgents/agentConfig';
import { submitSurfaceAgentFeedback } from '@/lib/agent/feedback';
import { AgentFeedback, InteractionFeedback } from '@/types';
import { toast } from '@/lib/notifications';

interface LiquidGlassCardProps {
  children: React.ReactNode;
  /** Semantic variants based on 3-tier glass system */
  variant?: 'neutral' | 'active' | 'success' | 'warning' | 'default';
  /** Glass tier for visual hierarchy (tier1=background, tier2=content, tier3=interactive) */
  tier?: 'tier1' | 'tier2' | 'tier3';
  state?: 'idle' | 'thinking' | 'working' | 'complete' | 'error';
  className?: string;
  id?: string;
  onClick?: () => void;
  hoverable?: boolean;
  animateOnLoad?: boolean;
}

const stateAnimations = {
  idle: 'opacity-100 scale-100',
  thinking: 'animate-thinking',
  working: 'animate-working',
  complete: 'scale-100 ring-2 ring-success/50',
  error: 'scale-100 ring-2 ring-destructive/50 animate-pulse'
};

const variantClasses = {
  neutral: 'liquid-glass glass-neutral',
  active: 'liquid-glass glass-active',
  success: 'liquid-glass glass-success',
  warning: 'liquid-glass glass-warning',
  default: 'liquid-glass',
};

export const LiquidGlassCard: React.FC<LiquidGlassCardProps> = ({
  children,
  variant = 'default',
  tier,
  state = 'idle',
  className,
  id,
  onClick,
  hoverable = true,
  animateOnLoad = true
}) => {
  const [isLoaded, setIsLoaded] = useState(!animateOnLoad);

  const handleLoad = useCallback(() => {
    if (animateOnLoad) {
      // Reduce delay for faster loading
      setTimeout(() => setIsLoaded(true), 50);
    }
  }, [animateOnLoad]);

  React.useEffect(() => {
    handleLoad();
  }, [handleLoad]);

  return (
    <motion.div
      initial={animateOnLoad ? { opacity: 0, scale: 0.95, y: 10 } : false}
      animate={isLoaded ? { opacity: 1, scale: 1, y: 0 } : undefined}
      transition={{
        type: "spring",
        stiffness: 350,
        damping: 25,
        mass: 0.8
      }}
      className={cn(
        variantClasses[variant],
        tier && `glass-${tier}`, // Apply tier class if specified
        stateAnimations[state],
        hoverable && 'cursor-pointer',
        'relative',
        // Only hide overflow for specific animation states
        (state === 'thinking' || state === 'working') ? 'overflow-hidden' : 'overflow-visible',
        className
      )}
      id={id}
      onClick={onClick}
      style={{
        '--spring-duration': '400ms',
        '--spring-damping': '20',
        '--spring-stiffness': '260'
      } as React.CSSProperties}
    >
      {/* Glass shimmer effect for loading/working states */}
      {(state === 'thinking' || state === 'working') && (
        <div className="absolute inset-0 animate-shimmer opacity-30" />
      )}

      {children}
    </motion.div>
  );
};

// Enhanced Message Component with Liquid Glass
interface LiquidGlassMessageProps {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp?: Date | number | { toDate: () => Date };
  loading?: boolean;
  error?: boolean;
  children?: React.ReactNode;
  compact?: boolean; // reduced padding and spacing when grouped with previous message
  showTimestamp?: boolean; // force show timestamp even when compact
  wide?: boolean; // allow wider message in focus mode
  agentHandoffData?: {
    selectedAgent: {
      id: string;
      name: string;
      type: 'crisis' | 'media' | 'social' | 'executive' | 'content' | 'general';
      description: string;
      expertise: string[];
    } | null;
    phase: 'selecting' | 'handoff' | 'processing' | 'complete';
    processingSteps: string[];
    statusText?: string;
    lastHandoff?: {
      fromAgentId: string;
      fromAgentName: string;
      toAgentId: string;
      toAgentName: string;
      timestamp: number;
    };
  };
  // Optional feedback wiring (non-breaking)
  onFeedback?: (messageId: string, feedback: InteractionFeedback) => Promise<void> | void;
  messageId?: string;
  agentMetadata?: {
    agentId: string;
    agentType: string;
    confidence: number;
    requiresApproval: boolean;
    tokensUsed: number;
    responseTime: number;
  };
}

export const LiquidGlassMessage: React.FC<LiquidGlassMessageProps> = React.memo(({
  role,
  content,
  timestamp,
  loading,
  error,
  children,
  compact = false,
  showTimestamp = true,
  wide = false,
  agentHandoffData,
  onFeedback,
  messageId,
  agentMetadata
}) => {
  // Use new semantic variants
  const messageVariant = role === 'user' ? 'active' : 'neutral';
  const messageState = error ? 'error' : 'idle';
  const [agentFeedbackGiven, setAgentFeedbackGiven] = useState(false);
  const [modifyNoteOpen, setModifyNoteOpen] = useState(false);
  const [modifyNote, setModifyNote] = useState('');
  const { activeClient } = useClient();
  const { currentUser } = useAuth();

  // Agent-specific feedback (updates autonomy metrics)
  const handleAgentFeedback = useCallback(async (fb: 'positive' | 'negative' | 'modified', note?: string) => {
    try {
      if (!agentMetadata || !activeClient || !currentUser || agentFeedbackGiven) return;
      const feedbackData: AgentFeedback = {
        agentId: agentMetadata.agentId,
        clientId: activeClient.id,
        userId: currentUser.uid,
        responseId: messageId || agentMetadata.agentId,
        feedback: fb,
        modifiedContent: note,
        timestamp: new Date().toISOString()
      };
      await agentConfigService.trackAgentPerformance(
        agentMetadata.agentId,
        activeClient.id,
        feedbackData,
        agentMetadata.tokensUsed,
        agentMetadata.responseTime
      );
      await submitSurfaceAgentFeedback({
        feedback: fb,
        messageId: messageId || undefined,
        runId: messageId || undefined,
        feedbackText: note,
      });
      if (fb === 'modified') {
        toast.success('Thanks! I’ll learn from your changes.');
        setModifyNote('');
        setModifyNoteOpen(false);
      }
      setAgentFeedbackGiven(true);
    } catch (e) {
      console.error('Failed to submit agent feedback:', e);
    }
  }, [agentMetadata, activeClient, currentUser, agentFeedbackGiven, messageId]);

  // General thumbs feedback (real-time learning) — payload kept identical to previous inline implementation
  const buildRatingFeedback = useCallback((rating: 'positive' | 'negative'): InteractionFeedback => ({
    rating,
    satisfaction: rating === 'positive' ? 5 : 2,
    realTimeLearning: { immediateUpdate: true, learningIntensity: rating === 'negative' ? 0.8 : 0.3 }
  }), []);

  return (
    <LiquidGlassCard
      variant={messageVariant}
      state={messageState}
      className={cn(
        "group p-3 mb-3 max-w-[90%]",
        compact && 'p-3 mb-2',
        wide && 'md:max-w-[80%] lg:max-w-[60%]',
        role === 'user' ? 'ml-auto' : 'mr-auto'
      )}
      hoverable={false}
    >
      <div className="space-y-2">
        {/* Message Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={cn(
              "w-2 h-2 rounded-full",
              role === 'user' ? 'bg-primary' : 'bg-info'
            )} />
            <span className="text-sm font-medium text-foreground opacity-90">
              {role === 'user' ? 'You' : 'PR Assistant'}
            </span>
          </div>
          {timestamp && (
            <span className={cn(
              "text-xs text-muted-foreground transition-opacity",
              (compact && !showTimestamp) ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'
            )}>
              {(() => {
                try {
                  // Handle different timestamp formats
                  let date: Date;
                  
                  if (timestamp instanceof Date) {
                    date = timestamp;
                  } else if (typeof timestamp === 'object' && timestamp.toDate) {
                    // Firestore timestamp
                    date = timestamp.toDate();
                  } else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
                    date = new Date(timestamp);
                  } else {
                    return 'Now';
                  }
                  
                  // Check if date is valid
                  if (isNaN(date.getTime())) {
                    return 'Now';
                  }
                  
                  return date.toLocaleTimeString([], { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  });
                } catch {
                  return 'Now';
                }
              })()} 
            </span>
          )}
        </div>

        {/* Message Content */}
        <div className="text-sm leading-relaxed text-foreground">
          {loading ? (
            agentHandoffData ? (
              <AgentHandoffAnimation
                selectedAgent={agentHandoffData.selectedAgent}
                phase={agentHandoffData.phase}
                processingSteps={agentHandoffData.processingSteps}
                statusText={agentHandoffData.statusText}
                lastHandoff={agentHandoffData.lastHandoff}
              />
            ) : (
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-current rounded-full animate-bounce" />
                <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                <span className="ml-2 text-muted-foreground">AI is crafting a response...</span>
              </div>
            )
          ) : (
            <ReactMarkdown
              components={{
                h1: ({children}) => <h1 className="text-xl font-bold mb-3 text-foreground">{children}</h1>,
                h2: ({children}) => <h2 className="text-lg font-semibold mb-2 text-foreground">{children}</h2>,
                h3: ({children}) => <h3 className="text-base font-medium mb-2 text-foreground">{children}</h3>,
                p: ({children}) => <p className="mb-3 last:mb-0 text-foreground">{children}</p>,
                strong: ({children}) => <strong className="font-semibold text-foreground">{children}</strong>,
                ul: ({children}) => <ul className="list-disc list-inside mb-3 space-y-1 text-foreground">{children}</ul>,
                ol: ({children}) => <ol className="list-decimal list-inside mb-3 space-y-1 text-foreground">{children}</ol>,
                li: ({children}) => <li className="text-foreground">{children}</li>,
                blockquote: ({children}) => <blockquote className="border-l-4 border-primary pl-4 py-2 mb-3 bg-muted/30 rounded-r text-foreground">{children}</blockquote>,
                code: ({children}) => <code className="bg-muted px-1 py-1 rounded text-sm font-mono text-foreground">{children}</code>,
                pre: ({children}) => <pre className="bg-muted p-3 rounded-lg overflow-x-auto max-h-64 overflow-y-auto mb-3 text-foreground">{children}</pre>,
                a: ({children, href}) => <a href={href} className="text-primary hover:underline" target="_blank" rel="noopener noreferrer nofollow">{children}</a>,
                img: ({src, alt}) => (
                  // PHASE-11 START
                  <span className="relative block w-full aspect-[4/3]">
                    <Image
                      src={String(src)}
                      alt={String(alt || '')}
                      fill
                      sizes="(max-width: 768px) 100vw, 700px"
                      className="rounded-md object-contain"
                      placeholder="blur"
                      blurDataURL={getImagePlaceholder(16, 12)}
                    />
                  </span>
                  // PHASE-11 END
                )
              }}
            >
              {content}
            </ReactMarkdown>
          )}
        </div>

        {/* Feedback controls for assistant messages */}
        {role === 'assistant' && !loading && onFeedback && messageId && (
          <MessageFeedbackControls
            messageId={messageId}
            onFeedback={onFeedback}
            buildRatingFeedback={buildRatingFeedback}
            onRatingGiven={agentMetadata ? (rating) => handleAgentFeedback(rating) : undefined}
            showThanks={agentFeedbackGiven}
            noteSentHint="Saved for future preference adaptation."
            className="mt-1"
          >
            {/* Modified (agent-specific) */}
            {agentMetadata && !agentFeedbackGiven && (
              <div className="relative flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon-xs"
                  onClick={() => setModifyNoteOpen(v => !v)}
                  className="text-muted-foreground hover:text-info"
                  aria-label="Modified this response"
                  title="I modified this response"
                >
                  <PencilIcon className="h-4 w-4" />
                </Button>
                {modifyNoteOpen && (
                  <div className="flex items-center gap-1 ml-1">
                    <InputV2
                      value={modifyNote}
                      onChange={(e) => setModifyNote(e.target.value)}
                      placeholder="Optional note (e.g., AP dates)"
                      size="sm"
                      className="w-[200px] text-xs"
                    />
                    <Button
                      variant="primary"
                      size="xs"
                      onClick={() => handleAgentFeedback('modified', modifyNote.trim() || undefined)}
                    >
                      Send
                    </Button>
                  </div>
                )}
              </div>
            )}
          </MessageFeedbackControls>
        )}

        {/* Additional Content */}
        {children}
      </div>
    </LiquidGlassCard>
  );
});

// Enhanced Tool Card with Liquid Glass
interface LiquidGlassToolCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  /** Semantic variant for the tool category */
  category?: 'active' | 'neutral' | 'warning' | 'success';
  onClick: () => void;
  active?: boolean;
  loading?: boolean;
}

export const LiquidGlassToolCard: React.FC<LiquidGlassToolCardProps> = ({
  title,
  description,
  icon,
  category,
  onClick,
  active,
  loading
}) => {
  const state = loading ? 'working' : active ? 'complete' : 'idle';

  return (
    <LiquidGlassCard
      variant={category}
      state={state}
      onClick={onClick}
      className="p-6 h-full"
    >
      <div className="space-y-4">
        {/* Tool Icon */}
        <div className="w-12 h-12 flex items-center justify-center">
          {icon}
        </div>

        {/* Tool Info */}
        <div className="space-y-2">
          <h3 className="font-semibold text-lg">{title}</h3>
          <p className="text-sm opacity-80 leading-relaxed">{description}</p>
        </div>

        {/* Progress indicator for loading state */}
        {loading && (
          <div className="w-full h-1 progress-glass overflow-hidden">
            <div className="h-full progress-fill w-full" />
          </div>
        )}
      </div>
    </LiquidGlassCard>
  );
};

// Enhanced Input with Liquid Glass
interface LiquidGlassInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  multiline?: boolean;
}

export const LiquidGlassInput: React.FC<LiquidGlassInputProps> = ({
  value,
  onChange,
  onSubmit,
  placeholder = "Type your message...",
  disabled,
  loading,
  multiline = true
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !disabled) {
      e.preventDefault();
      onSubmit();
    }
  };

  const Component = multiline ? 'textarea' : 'input';

  return (
    <div className={cn(
      "chat-input-glass p-4 transition-all duration-300",
      isFocused && "ring-2 ring-primary/30"
    )}>
      <Component
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={placeholder}
        disabled={disabled || loading}
        className={cn(
          "w-full bg-transparent border-none outline-none resize-none",
          "placeholder:text-muted-foreground text-foreground",
          multiline && "min-h-[20px] max-h-32"
        )}
        {...(multiline && { rows: 1 })}
      />
      
      {loading && (
        <div className="mt-2 flex items-center gap-2 text-sm opacity-60">
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          Processing...
        </div>
      )}
    </div>
  );
};

export default LiquidGlassCard;
